/**
 * GlobalSearchOverlay — instant search across all products and categories.
 * Fuzzy matches title/caption/description/material/category label.
 * Opens as a full-screen overlay from the navbar search icon.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight } from "lucide-react";
import { useEffectiveProducts, useEffectiveCategories, resolveImage } from "@/context/PricesContext";
import { PRODUCTS, CATEGORIES } from "@/data/anabiya";
import { useCurrency } from "@/context/CurrencyContext";

const norm = (s) => (s || "").toString().toLowerCase();

export const GlobalSearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const products = useEffectiveProducts(PRODUCTS);
  const cats = useEffectiveCategories(CATEGORIES);
  const { format } = useCurrency();

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const query = norm(q).trim();
    if (!query || query.length < 1) {
      return { products: [], categories: [] };
    }
    const words = query.split(/\s+/).filter(Boolean);
    const matchAll = (haystack) => words.every((w) => norm(haystack).includes(w));

    const productHits = products
      .map((p) => {
        const hay = [p.title, p.caption, p.description, p.material, p.category, ...(p.fabric || []), ...(p.sizes || []), ...((p.colours || []).map((c) => c?.name))].join(" ");
        return { p, hit: matchAll(hay), titleMatch: matchAll(p.title || "") };
      })
      .filter((x) => x.hit)
      .sort((a, b) => Number(b.titleMatch) - Number(a.titleMatch))
      .slice(0, 24)
      .map((x) => x.p);

    const categoryHits = cats.filter((c) => matchAll(`${c.label} ${c.slug}`)).slice(0, 8);
    return { products: productHits, categories: categoryHits };
  }, [q, products, cats]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-forest/90 backdrop-blur-xl overflow-y-auto"
          data-testid="global-search-overlay"
          onClick={onClose}
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-24" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="overline text-champagne/70 mb-2">§ Catalogue search</div>
                <p className="text-champagne/60 text-sm">Try "duvet", "hotel linen", "linen dress"…</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="close-search"
                className="p-2 -mr-2 text-champagne/70 hover:text-champagne"
                aria-label="Close search"
              >
                <X size={20} strokeWidth={1.6} />
              </button>
            </div>

            <div className="relative">
              <Search size={18} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-champagne/70" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products, categories, fabrics…"
                className="w-full bg-transparent border-b border-champagne/40 pl-12 pr-4 py-4 text-2xl md:text-3xl text-champagne font-display placeholder:text-champagne/40 focus:outline-none focus:border-champagne"
                data-testid="search-input"
                autoComplete="off"
              />
            </div>

            {q.trim() && (
              <div className="mt-8">
                {results.categories.length > 0 && (
                  <div className="mb-8">
                    <div className="overline text-gold text-xs mb-3">§ Categories · {results.categories.length}</div>
                    <div className="flex flex-wrap gap-2">
                      {results.categories.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/shop?category=${c.slug}`}
                          onClick={onClose}
                          className="inline-flex items-center gap-2 border border-champagne/30 text-champagne overline text-xs px-4 py-2 rounded-full hover:bg-champagne hover:text-forest transition-colors"
                          data-testid={`search-cat-${c.slug}`}
                        >
                          {c.label} <ArrowRight size={12} strokeWidth={1.6} />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="overline text-gold text-xs mb-4">
                  § Products · {results.products.length}
                </div>
                {results.products.length === 0 ? (
                  <p className="text-champagne/60 text-sm italic" data-testid="search-empty">
                    Nothing matches "{q}". Try a shorter word or browse the <Link to="/shop" onClick={onClose} className="underline decoration-champagne/40 underline-offset-4 hover:decoration-champagne">full catalogue →</Link>
                  </p>
                ) : (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {results.products.map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={`/product/${p.slug}`}
                          onClick={onClose}
                          data-testid={`search-result-${p.slug}`}
                          className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-champagne/20 hover:border-champagne/50 transition-colors p-3 rounded-md"
                        >
                          <div className="w-16 h-16 shrink-0 bg-champagne/10 overflow-hidden rounded">
                            {p.images?.[0] && (
                              <img src={resolveImage(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display text-champagne text-base leading-tight truncate">{p.title}</div>
                            <div className="text-champagne/50 text-xs mt-0.5">{p.caption || p.category}</div>
                            <div className="text-gold text-xs tabnum mt-1">{format(p.price)}</div>
                          </div>
                          <ArrowRight size={14} strokeWidth={1.6} className="text-champagne/50 group-hover:text-champagne shrink-0" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalSearchOverlay;
